import type { EnvironmentId, ThreadId } from "@cmmd-forge/contracts";
import { useState, type ReactElement } from "react";
import { EllipsisIcon, LinkIcon, ListTodoIcon, TargetIcon } from "lucide-react";

import { cn } from "~/lib/utils";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { ComposerTaskAnchorPicker } from "./ComposerTaskAnchorPicker";
import { STRIP_CONTROL_CLASS } from "./composerControlsStripChrome";
import { isThreadGoalEditable, threadGoalMenuLabel, ThreadGoalChip } from "./ThreadGoalChip";

/** What the strip's overflow menu can offer, when a thread offers it at all. */
export interface ComposerControlsStripOverflowProps {
  readonly taskAnchor: {
    readonly cmmdProjectId: string;
    readonly environmentId: EnvironmentId;
    readonly threadId: ThreadId;
    readonly currentTaskId: string | null;
    readonly disabled: boolean;
  } | null;
  readonly planSidebar: {
    readonly label: string;
    readonly open: boolean;
    readonly onToggle: () => void;
  } | null;
  readonly goal?:
    | {
        readonly goal: string | null | undefined;
        readonly environmentId: EnvironmentId;
        readonly threadId: ThreadId;
        readonly taskManaged: boolean;
        readonly onUpdateGoal?: (goal: string | null) => Promise<void>;
      }
    | null
    | undefined;
}

/**
 * The strip's overflow menu: the CMMD board task anchor and the plan sidebar
 * toggle.
 *
 * Both used to sit in the visible footer, which is where t3code puts neither.
 * They are Forge's own controls, so they move here rather than being dropped.
 * Nothing to offer means no trigger at all: an ellipsis that opens an empty
 * menu is worse than no ellipsis.
 */
export function ComposerControlsStripOverflow({
  overflow,
}: {
  readonly overflow: ComposerControlsStripOverflowProps | undefined;
}): ReactElement | null {
  const taskAnchor = overflow?.taskAnchor ?? null;
  const planSidebar = overflow?.planSidebar ?? null;
  const goal = overflow?.goal ?? null;
  if (taskAnchor === null && planSidebar === null && goal === null) return null;
  return <OverflowMenu taskAnchor={taskAnchor} planSidebar={planSidebar} goal={goal} />;
}

function OverflowMenu({
  taskAnchor,
  planSidebar,
  goal,
}: {
  readonly taskAnchor: ComposerControlsStripOverflowProps["taskAnchor"];
  readonly planSidebar: ComposerControlsStripOverflowProps["planSidebar"];
  readonly goal: ComposerControlsStripOverflowProps["goal"];
}): ReactElement {
  const [taskAnchorOpen, setTaskAnchorOpen] = useState(false);
  const [goalOpen, setGoalOpen] = useState(false);
  const [triggerElement, setTriggerElement] = useState<HTMLElement | null>(null);
  const canEditGoal = goal ? isThreadGoalEditable(goal) : false;

  return (
    <>
      <Menu>
        <MenuTrigger
          ref={setTriggerElement}
          aria-label="More thread controls"
          className={cn(
            STRIP_CONTROL_CLASS,
            "inline-flex items-center justify-center px-1.5 md:w-6",
          )}
        >
          <EllipsisIcon aria-hidden="true" className="size-3.5" />
        </MenuTrigger>
        <MenuPopup align="end" side="top">
          <OverflowMenuActions
            taskAnchor={taskAnchor}
            planSidebar={planSidebar}
            goal={goal}
            canEditGoal={canEditGoal}
            onOpenTaskAnchor={() => setTaskAnchorOpen(true)}
            onOpenGoal={() => setGoalOpen(true)}
          />
        </MenuPopup>
      </Menu>
      {/* Outside the menu, not inside it. A menu item unmounts its subtree the
          moment the menu closes, and the menu closes on the very click that is
          supposed to open the picker. So the picker owns its open state here
          and anchors itself back to the ellipsis. */}
      {taskAnchor ? (
        <ComposerTaskAnchorPicker
          projectId={taskAnchor.cmmdProjectId}
          environmentId={taskAnchor.environmentId}
          threadId={taskAnchor.threadId}
          currentTaskId={taskAnchor.currentTaskId}
          disabled={taskAnchor.disabled}
          open={taskAnchorOpen}
          onOpenChange={setTaskAnchorOpen}
          anchor={triggerElement}
        />
      ) : null}
      {goal && canEditGoal ? (
        <ThreadGoalChip
          goal={goal.goal}
          environmentId={goal.environmentId}
          threadId={goal.threadId}
          taskManaged={goal.taskManaged}
          editor={{ open: goalOpen, anchor: triggerElement, onOpenChange: setGoalOpen }}
          {...(goal.onUpdateGoal ? { onUpdateGoal: goal.onUpdateGoal } : {})}
        />
      ) : null}
    </>
  );
}

function OverflowMenuActions(props: {
  readonly taskAnchor: ComposerControlsStripOverflowProps["taskAnchor"];
  readonly planSidebar: ComposerControlsStripOverflowProps["planSidebar"];
  readonly goal: ComposerControlsStripOverflowProps["goal"];
  readonly canEditGoal: boolean;
  readonly onOpenTaskAnchor: () => void;
  readonly onOpenGoal: () => void;
}): ReactElement {
  return (
    <>
      {props.taskAnchor ? (
        <TaskAnchorMenuItem taskAnchor={props.taskAnchor} onOpen={props.onOpenTaskAnchor} />
      ) : null}
      {props.planSidebar ? <PlanSidebarMenuItem planSidebar={props.planSidebar} /> : null}
      {props.goal ? (
        <GoalMenuItem goal={props.goal} canEdit={props.canEditGoal} onOpen={props.onOpenGoal} />
      ) : null}
    </>
  );
}

function TaskAnchorMenuItem(props: {
  readonly taskAnchor: NonNullable<ComposerControlsStripOverflowProps["taskAnchor"]>;
  readonly onOpen: () => void;
}): ReactElement {
  const { taskAnchor } = props;
  return (
    <MenuItem disabled={taskAnchor.disabled} onClick={props.onOpen}>
      <LinkIcon aria-hidden="true" className="size-4 shrink-0" />
      {taskAnchor.currentTaskId === null ? "Attach a task" : `Task #${taskAnchor.currentTaskId}`}
    </MenuItem>
  );
}

function GoalMenuItem(props: {
  readonly goal: NonNullable<ComposerControlsStripOverflowProps["goal"]>;
  readonly canEdit: boolean;
  readonly onOpen: () => void;
}): ReactElement {
  return (
    <MenuItem disabled={!props.canEdit} onClick={props.onOpen}>
      <TargetIcon aria-hidden="true" className="size-4 shrink-0" />
      {threadGoalMenuLabel(props.goal)}
    </MenuItem>
  );
}

function PlanSidebarMenuItem({
  planSidebar,
}: {
  readonly planSidebar: NonNullable<ComposerControlsStripOverflowProps["planSidebar"]>;
}): ReactElement {
  const noun = planSidebar.label.toLowerCase();
  return (
    <MenuItem onClick={planSidebar.onToggle}>
      <ListTodoIcon aria-hidden="true" className="size-4 shrink-0" />
      {planSidebar.open ? `Hide ${noun} sidebar` : `Show ${noun} sidebar`}
    </MenuItem>
  );
}
